import type { BlockPromptContext } from './types.ts';
import { buildProgressionClause, buildExampleClause, buildCrossMonthClause } from './types.ts';
import { buildPersonalizationClause } from './personalization-helpers.ts';
import { SOURCE_LANGUAGES, TARGET_LANGUAGES } from '../../constants/languages.ts';
import { safeText } from '../../sanitize.ts';

export function buildGuidedConversationPrompt(ctx: BlockPromptContext): string {
  const source = SOURCE_LANGUAGES.find((l) => l.code === ctx.profile.native_language);
  const target = TARGET_LANGUAGES.find((l) => l.code === ctx.profile.target_language);

  const previousVocabSection =
    ctx.previousVocabulary.length > 0
      ? `\n## Previously Introduced Vocabulary (reuse some naturally in the dialogue)

${ctx.previousVocabulary
  .slice(0, 20)
  .map((v) => `- ${v}`)
  .join('\n')}`
      : '';

  return `Generate a **Guided Conversation** exercise block for a ${ctx.profile.level.toUpperCase()} ${target?.name} learner whose native language is ${source?.name}.

## Theme
Workbook theme: "${ctx.workbookTheme}"
Sub-theme for this block: "${ctx.subTheme}"

## Block Position
This is block ${ctx.blockOrder} of ${ctx.totalBlocks}.
${buildPersonalizationClause(ctx.profile, ctx.blockOrder, ctx.isPreview)}${previousVocabSection}

## Requirements

1. Create a **realistic scenario** for the sub-theme "${ctx.subTheme}". The situation should feel authentic and useful for a traveler/expat.
2. The \`situation\` object needs:
   - \`description\`: 2-3 sentences in ${target?.name} describing the scene
   - \`descriptionNative\`: The same scene described in ${source?.name} (natural translation, not word-for-word)
   - \`emoji\`: A single contextual emoji
3. Generate **8 conversation turns** that alternate between the other speaker and the learner (<user_input>${safeText(ctx.profile.display_name)}</user_input>).
   - The conversation MUST start with the \`other\` speaker.
   - Aim for approximately **4 "other" turns and 4 "learner" turns**.
   - Every "learner" turn has \`isPrompt: true\` and a \`starterHint\` — a sentence starter that gives the learner a framework.
   - Every "other" turn has \`isPrompt: false\`.
4. Each turn needs:
   - \`lineTarget\`: The complete line in ${target?.name}
   - \`lineNative\`: A natural translation in ${source?.name}
   - For learner turns: \`starterHint\` gives 1-2 sentence starters separated by " / " for the learner to choose from
5. Include **3-5 vocabularyItems** — key words from the conversation that the learner should know.
6. Optionally include a \`tip\` — a cultural or grammatical tip in ${source?.name} related to the conversation.

## Output Format

Return a single JSON object matching this TypeScript interface exactly:

\`\`\`typescript
interface GuidedConversationBlock {
  id: string;                    // Generate a UUID v4
  type: "guided_conversation";   // Literal
  order: ${ctx.blockOrder};
  title: string;                 // In ${target?.name} — the scenario title
  titleNative: string;           // In ${source?.name} — with context descriptor
  theme: "${ctx.subTheme}";
  estimatedPages: 2;
  situation: {
    description: string;         // In ${target?.name}
    descriptionNative: string;   // In ${source?.name}
    emoji: string;
  };
  turns: Array<{
    number: number;              // 1-8
    role: "other" | "learner";
    roleName: string;            // e.g., "Doctora García" or the learner's name
    lineTarget: string;          // In ${target?.name}
    lineNative: string;          // In ${source?.name}
    isPrompt: boolean;           // true for learner turns
    starterHint?: string;        // Only for learner turns
  }>;
  vocabularyItems: Array<{
    target: string;
    native: string;
  }>;
  tip?: {
    emoji: string;
    text: string;                // In ${source?.name}
  };
}
\`\`\`

Respond with ONLY the JSON object.${buildProgressionClause(ctx)}${buildExampleClause(ctx, 'guided_conversation')}${buildCrossMonthClause(ctx)}`;
}
